import { useCallback, useEffect, useRef, useState } from "react";
import type { Order } from "@/lib/projects";
import {
  fetchOrderBookedHoursDetail,
  type BookedHoursDetail,
  type BookedHoursRow,
} from "@/lib/bookedHours";
import { useRealtime } from "@/lib/useRealtime";
import { realtimeEventConcernsOrder } from "@/projectOverview/realtimeOrderEvent";

const EMPTY: BookedHoursDetail = {
  totalHours: 0,
  rows: [],
  heroError: false,
  subcontractorError: false,
  employeeError: false,
};

export function useOrderBookedHours(project: Order | null, enabled = true) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [detail, setDetail] = useState<BookedHoursDetail>(EMPTY);
  const projectRef = useRef(project);
  projectRef.current = project;
  const loadedIdRef = useRef<number | null>(null);

  const load = useCallback(async (options?: { silent?: boolean }) => {
    const current = projectRef.current;
    if (!current?.id || !enabled) {
      loadedIdRef.current = null;
      setDetail(EMPTY);
      setLoading(false);
      setError(false);
      return;
    }
    if (!options?.silent) setLoading(true);
    setError(false);
    try {
      const next = await fetchOrderBookedHoursDetail(current, {
        includeEmployeeTimes: true,
      });
      const hasLoadError =
        (next.heroError || next.subcontractorError || Boolean(next.employeeError)) &&
        next.rows.length === 0;
      loadedIdRef.current = current.id;
      setDetail((prev) => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
      setError(hasLoadError);
    } catch {
      // Stilles Nachladen: letzten bekannten Stand behalten statt „–" zu zeigen.
      if (!options?.silent) {
        setDetail(EMPTY);
        setError(true);
      }
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  const projectId = project?.id ?? null;
  const heroProjectId = project?.hero_project_id ?? null;

  useEffect(() => {
    const silent = loadedIdRef.current != null && loadedIdRef.current === projectId;
    void load({ silent });
  }, [load, projectId, heroProjectId]);

  // Realtime-Änderungen still einpflegen – die Kachel flackert dann nicht.
  useRealtime({
    scopes: ["auftraege:list", "employee-times:list"],
    domains: ["auftraege", "hero", "timesheets", "employee-times"],
    events: [
      "auftraege_changed",
      "hero_changed",
      "timesheets_changed",
      "employee-times_created",
      "employee-times_updated",
      "employee-times_deleted",
      "employee-times_changed",
    ],
    enabled: enabled && !!project?.id,
    onChange: (payload) => {
      if (!realtimeEventConcernsOrder(payload, projectRef.current?.id ?? null)) return;
      void load({ silent: true });
    },
  });

  return {
    loading,
    error,
    totalHours: detail.totalHours,
    rows: detail.rows as BookedHoursRow[],
    heroError: detail.heroError,
    subcontractorError: detail.subcontractorError,
    employeeError: Boolean(detail.employeeError),
    refetch: load,
  };
}
